import React, { ChangeEvent, ClipboardEvent, KeyboardEvent, useState } from 'react';
import {
    removeInvisibleCharsOnPaste,
    updateControl,
    validateControl,
    validateForm
} from '../../../../../domain/validation/validation';
import FormTextField from '../../../../../ui/forms/fields/text-field';
import { couponActions, rootActions, useAppDispatch, useAppSelector } from '../../data/store';
import Preloader from '../../../../../ui/svg/common/preloader';
import { IFormControl } from '../../../../../ui/forms/interfaces';
import { addCoupons } from '../../data/data-service';
import ValidationErrors from '../../../../../domain/validation/server-errors/validation-errors';
import { getLocalData } from '../../domain/shopping-cart-address-provider';
import { sendGAEvent } from '../../../../../domain/marketing/gtm-provider';

const CouponForm = () => {

    const dispatch = useAppDispatch();
    const couponControl = useAppSelector(store => store.coupons.couponControl);
    const cartStep = useAppSelector(store => store.root.cartStep);

    const [loading, setLoading] = useState(false);
    const [serverErrors, setServerErrors] = useState<string[]>([]);

    /**
     * and enter / return keys
     */
    const handleEnter = async (evt: KeyboardEvent<HTMLInputElement>) => {
        if(evt.key === 'Enter') {
            await addCouponHandler();
            return;
        }

        if(evt.key === 'Escape') {
            // remove coupon value from the text box
            dispatch(
                couponActions.setCouponControl(
                    updateControl(couponControl, '', false)
                )
            );

            // remove server errors
            dispatch(
                couponActions.main({
                    showCouponNavError: false,
                })
            );

            setServerErrors([]);
            return;
        }
    };

    /**
     * empty errors on click
     */
    const handleFieldClick = () =>{
        dispatch(
            couponActions.main({
                showCouponNavError: false,
            })
        );

        setServerErrors([]);
    };

    /**
     * On input change
     */
    const handleChangeEvent = (evt: ChangeEvent<HTMLInputElement>) => {
        dispatch(
            couponActions.setCouponControl(
                updateControl(couponControl, evt.target.value, !couponControl.isValid)
            )
        );

        dispatch(
            couponActions.main({
                showCouponNavError: false,
            })
        );
    };

    /**
     * validate on blur
     */
    const handleBlurEvent = () => {
        dispatch(
            couponActions.setCouponControl(
                validateControl(couponControl)
            )
        );
    };

    /**
     * remove invisible characters on paste event
     */
    const handlePasteEvent = (evt: ClipboardEvent<HTMLInputElement>) => {
        dispatch(
            couponActions.setCouponControl(
                updateControl(couponControl, removeInvisibleCharsOnPaste(evt))
            )
        );
    };

    /**
     * handle 'add coupon' button click
     */
    const addCouponHandler = async () => {

        dispatch(
            couponActions.main({
                showCouponNavError: false,
            })
        );

        setServerErrors([]);

        // apply validations
        const isValid = validateForm([
            [
                couponControl,
                (updatedControl: IFormControl) => {
                    dispatch(couponActions.setCouponControl(updatedControl));
                }
            ],
        ]);
        if(!isValid) return;

        setLoading(true);

        // send data to the server side
        const response = await addCoupons(
            [couponControl.value.trim()],
            getLocalData()
        );

        setLoading(false);

        if(!response || response.statusCode !== 200 || !response.data.isValid || !response.data.relatedData){
            setServerErrors(response?.data?.errors || ['An error has occurred.']);
            return;
        }

        // update the whole cart data
        dispatch(
            rootActions.setCartData(response.data.relatedData)
        );

        // remove coupon value from the text box
        dispatch(
            couponActions.setCouponControl(
                updateControl(couponControl, '', false)
            )
        );

        sendGAEvent('Apply Coupon', `Step ${ cartStep }`);
    };

    return (
        <div>

            <FormTextField
                id={ 'coupon-tb' }
                label={ 'Enter Coupon Code' }
                control={ couponControl }
                textBoxClasses="border-green rounded text-gray-500"
                rootClasses="mb-0"
                qa={ 'cart-coupon-tb' }
                onChange={ handleChangeEvent }
                onBlur={ handleBlurEvent }
                onClick={ handleFieldClick }
                onKeyUp={ handleEnter }
                onPaste={ handlePasteEvent }
            />

            <div className="flex justify-end">
                {
                    !loading &&
                    <button
                        type="button"
                        data-qa="cart-apply-coupon"
                        className="cursor-pointer text-gray-700 font-bold"
                        onClick={ addCouponHandler }>
                        Apply
                    </button>
                }

                { loading && <Preloader width="24px" height="24px" color="#4f4f4f" /> }
            </div>

            {
                serverErrors && serverErrors.length > 0 && (
                    <div className="error-box text-sm my-4" data-qa="cart-coupon-error">
                        <ValidationErrors errors={ serverErrors } />
                    </div>
                )
            }

        </div>
    )
};

export default CouponForm;